import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/ui/section-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { IconAlertTriangle, IconPlus } from "@tabler/icons-react";
import { ToeCheckCard } from "@/components/toe-check-card";
import { ToeCheckPhotoDrawer } from "@/components/toe-check-photo-drawer";
import { YawCheckCard } from "@/components/yaw-check-card";
import { YawCheckFormDrawer } from "@/components/yaw-check-form-drawer";
import { usePhotoCategories } from "@/api/hooks";
import { flattenCategories } from "@/lib/photo-categories";
import { IconHistory } from "@tabler/icons-react";
import { asToeCheckId, asYawCheckId, type BuildId, type ToeCheckId } from "@/types/identifiers";
import type { ToeCheckActions } from "@/hooks/use-toe-check-actions";
import type { YawCheckActions } from "@/hooks/use-yaw-check-actions";
import type { Action } from "@/providers/auth-provider";
import type { components } from "@/api/generated/schema";

const noop = () => {};
const asyncNoop = () => Promise.resolve();
const noopYawActions: YawCheckActions = {
  handleCreate: asyncNoop,
  handleUpdate: asyncNoop,
  handleSignOff: noop,
  handleUnlock: noop,
  handleDelete: noop,
  isCreating: false,
  isUpdating: false,
  isSigningOff: false,
  isUnlocking: false,
  isDeleting: false,
  yawChecks: [],
  signedOffCount: 0,
};

type ToeCheck = components["schemas"]["ToeCheckResponse"];
type YawCheck = components["schemas"]["YawCheckResponse"];
type YawCheckPreviousEvent = components["schemas"]["YawCheckPreviousEventResponse"];

interface ChecksSectionProps {
  buildId: BuildId;
  toeChecks: ToeCheck[] | undefined;
  yawChecks: YawCheck[] | undefined;
  isLoading: boolean;
  toeActions: ToeCheckActions;
  yawActions: YawCheckActions;
  can: (action: Action) => boolean;
  readOnly?: boolean;
  previousEventYawCheck?: YawCheckPreviousEvent;
}

export function ChecksSection({
  buildId,
  toeChecks,
  yawChecks,
  isLoading,
  toeActions,
  yawActions,
  can,
  readOnly = false,
  previousEventYawCheck,
}: ChecksSectionProps) {
  // Toe check photo drawer state
  const [toePhotoCheckId, setToePhotoCheckId] = useState<ToeCheckId | null>(null);
  const [toeUploadingIds, setToeUploadingIds] = useState<Set<ToeCheckId>>(new Set());

  // Yaw check state
  const [yawFormOpen, setYawFormOpen] = useState(false);
  const [yawFormKey, setYawFormKey] = useState(0);
  const [editingYawCheck, setEditingYawCheck] = useState<YawCheck | null>(null);

  // Photo category
  const { data: allCategories } = usePhotoCategories();
  const checksRoot = allCategories?.find((r) => r.name === "Checks");
  const checksCategoryId = flattenCategories(checksRoot?.children ?? [])[0]?.id;
  const missingCategory = allCategories && !checksCategoryId;

  const openYawCreate = () => {
    setEditingYawCheck(null);
    setYawFormKey((k) => k + 1);
    setYawFormOpen(true);
  };

  const openYawEdit = (check: YawCheck) => {
    setEditingYawCheck(check);
    setYawFormKey((k) => k + 1);
    setYawFormOpen(true);
  };

  const handleYawFormSubmit = async (
    data: components["schemas"]["YawCheckCreate"] | components["schemas"]["YawCheckUpdate"],
  ) => {
    // Throws on mutation failure — drawer awaits this and stays open with values
    // intact if it rejects. On success, the drawer closes itself via onOpenChange,
    // which clears editingYawCheck below.
    // The drawer sends a partial YawCheckUpdate in edit mode and a full
    // YawCheckCreate in create mode; the union is narrowed by the editing flag.
    if (editingYawCheck) {
      await yawActions.handleUpdate(
        asYawCheckId(editingYawCheck.id),
        data as components["schemas"]["YawCheckUpdate"],
      );
    } else {
      await yawActions.handleCreate(data as components["schemas"]["YawCheckCreate"]);
    }
  };

  return (
    <>
      {missingCategory && (
        <Alert variant="destructive" className="mt-4">
          <IconAlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Photo category "Checks" is not configured. Check photos cannot be uploaded until it is seeded.
          </AlertDescription>
        </Alert>
      )}

      {/* Toe Checks */}
      <div className="mt-6 flex items-center justify-between gap-4">
        <SectionHeader className="mb-0">Toe Checks</SectionHeader>

        {!readOnly && can("manage-calibrations") && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            onClick={toeActions.handleCreate}
            disabled={toeActions.isCreating}
          >
            {toeActions.isCreating
              ? <span className="mr-1.5 h-3 w-3 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
              : <IconPlus className="mr-1.5 h-3 w-3" />
            }
            {toeActions.isCreating ? "Recording…" : "Record Toe Check"}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2">
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !toeChecks || toeChecks.length === 0 ? (
        <EmptyState message='No toe checks yet. Click "Record Toe Check" to get started.' className="mt-4" />
      ) : (
        <div className="mt-4 space-y-2">
          {toeChecks.map((check) => {
            const checkId = asToeCheckId(check.id);
            return (
              <ToeCheckCard
                key={check.id}
                check={check}
                actions={toeActions}
                can={can}
                isUploading={toeUploadingIds.has(checkId)}
                onPhotoClick={() => setToePhotoCheckId(checkId)}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      )}

      {/* YAW Checks */}
      <div className="mt-8 flex items-center justify-between gap-4">
        <SectionHeader className="mb-0">YAW Checks</SectionHeader>

        {!readOnly && can("manage-calibrations") && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            onClick={openYawCreate}
            disabled={yawActions.isCreating}
          >
            <IconPlus className="mr-1.5 h-3 w-3" />
            Record YAW Check
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2">
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : !yawChecks || yawChecks.length === 0 ? (
        <EmptyState message='No YAW checks yet. Click "Record YAW Check" to get started.' className="mt-4" />
      ) : (
        <div className="mt-4 space-y-2">
          {yawChecks.map((check) => (
              <YawCheckCard
                key={check.id}
                check={check}
                actions={yawActions}
                can={can}
                onEditClick={() => openYawEdit(check)}
                readOnly={readOnly}
              />
          ))}
        </div>
      )}

      {/* Previous event yaw check */}
      {previousEventYawCheck && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
            <IconHistory className="h-3.5 w-3.5" />
            <span>Previous Event — {previousEventYawCheck.event_name}</span>
          </div>
          <div className="opacity-60">
            <YawCheckCard
              check={previousEventYawCheck.check}
              actions={noopYawActions}
              can={() => false}
              readOnly
            />
          </div>
        </div>
      )}

      {/* Toe check photo drawer */}
      {toePhotoCheckId && checksCategoryId && (
        <ToeCheckPhotoDrawer
          open={!!toePhotoCheckId}
          onOpenChange={(open) => {
            if (!open) setToePhotoCheckId(null);
          }}
          buildId={buildId}
          checkId={toePhotoCheckId}
          categoryId={checksCategoryId}
          photo={toeChecks?.find((c) => c.id === toePhotoCheckId)?.photo}
          onLoadingChange={(loading) => {
            setToeUploadingIds((prev) => {
              const next = new Set(prev);
              if (loading) next.add(toePhotoCheckId!);
              else next.delete(toePhotoCheckId!);
              return next;
            });
          }}
        />
      )}

      {/* YAW check form drawer */}
      <YawCheckFormDrawer
        key={yawFormKey}
        open={yawFormOpen}
        onOpenChange={(open) => {
          setYawFormOpen(open);
          if (!open) setEditingYawCheck(null);
        }}
        onSubmit={handleYawFormSubmit}
        isSubmitting={yawActions.isCreating || yawActions.isUpdating}
        buildId={buildId}
        initialData={editingYawCheck ?? undefined}
        latestCheckId={yawChecks?.[0]?.id ?? null}
      />

    </>
  );
}
